import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { adminApi } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  Users,
  IndianRupee,
  TrendingUp,
  Activity as ActivityIcon,
  Search,
  Mail,
  Loader2,
  ShoppingBag,
  UserPlus,
  CalendarClock,
} from 'lucide-react';

const RUPEE = (n) => '₹' + Number(n || 0).toLocaleString('en-IN');
const DATE = (s) => (s ? new Date(s).toLocaleString() : '—');
const SHORT_DATE = (s) => {
  if (!s) return '—';
  try {
    return new Date(s).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' });
  } catch (_) {
    return s;
  }
};

export default function AdminActivityTab() {
  const [summary, setSummary] = useState(null);
  const [purchases, setPurchases] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userSearch, setUserSearch] = useState('');
  const [purchaseFilter, setPurchaseFilter] = useState('all'); // all | paid | pending
  const [purchaseQuery, setPurchaseQuery] = useState('');

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [s, p, u] = await Promise.all([
        adminApi.get('/admin/activity/summary'),
        adminApi.get('/admin/activity/purchases', { params: { limit: 200 } }),
        adminApi.get('/admin/activity/users', { params: { limit: 200 } }),
      ]);
      setSummary(s.data);
      setPurchases(p.data.items || []);
      setUsers(u.data.items || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load activity');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const filteredPurchases = useMemo(() => {
    let rows = purchases;
    if (purchaseFilter !== 'all') rows = rows.filter((r) => (r.status || '').toLowerCase() === purchaseFilter);
    const q = purchaseQuery.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        (r.buyer_email || '').toLowerCase().includes(q) ||
        (r.buyer_name || '').toLowerCase().includes(q) ||
        (r.plan_id || '').toLowerCase().includes(q) ||
        (r.plan_name || '').toLowerCase().includes(q) ||
        (r.id || '').toLowerCase().includes(q),
    );
  }, [purchases, purchaseQuery, purchaseFilter]);

  const filteredUsers = useMemo(() => {
    const q = userSearch.trim().toLowerCase();
    if (!q) return users;
    return users.filter(
      (u) =>
        (u.email || '').toLowerCase().includes(q) ||
        (u.name || '').toLowerCase().includes(q),
    );
  }, [users, userSearch]);

  if (loading && !summary) {
    return (
      <div className="grid min-h-[320px] place-items-center text-neutral-500">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-activity-root">
      {/* Summary cards */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <SummaryCard
          testId="activity-card-revenue"
          icon={<IndianRupee className="h-5 w-5" />}
          label="Total Revenue"
          value={RUPEE(summary?.revenue_total)}
          sub={`${summary?.paid_orders || 0} paid orders`}
          accent="from-red-600 to-rose-500"
        />
        <SummaryCard
          testId="activity-card-paid-users"
          icon={<ShoppingBag className="h-5 w-5" />}
          label="Paid Users"
          value={summary?.users_paid ?? 0}
          sub={`of ${summary?.users_total ?? 0} total`}
          accent="from-emerald-500 to-teal-500"
        />
        <SummaryCard
          testId="activity-card-active-today"
          icon={<ActivityIcon className="h-5 w-5" />}
          label="Active Today"
          value={summary?.active_today ?? 0}
          sub="distinct users (tests + orders)"
          accent="from-amber-500 to-orange-500"
        />
        <SummaryCard
          testId="activity-card-signups"
          icon={<UserPlus className="h-5 w-5" />}
          label="New Users · 7d"
          value={summary?.new_users_7d ?? 0}
          sub={DATE(summary?.as_of)}
          accent="from-sky-500 to-indigo-500"
        />
      </div>

      {/* Purchases + Users nested tabs */}
      <Tabs defaultValue="purchases" className="space-y-4">
        <TabsList className="grid w-full max-w-md grid-cols-2 bg-neutral-900">
          <TabsTrigger value="purchases" data-testid="activity-tab-purchases">
            <ShoppingBag className="mr-1.5 h-4 w-4" />
            Purchases
          </TabsTrigger>
          <TabsTrigger value="users" data-testid="activity-tab-users">
            <Users className="mr-1.5 h-4 w-4" />
            Users
          </TabsTrigger>
        </TabsList>

        <TabsContent value="purchases" className="space-y-3">
          <Card className="border-neutral-800 bg-neutral-950/70">
            <CardContent className="space-y-4 p-5 sm:p-6">
              <div className="flex flex-wrap items-center gap-3">
                <div className="font-display text-base font-semibold">Recent orders</div>
                <Badge variant="secondary" data-testid="activity-purchases-count">
                  {filteredPurchases.length} / {purchases.length}
                </Badge>
                <div className="ml-auto flex flex-wrap items-center gap-2">
                  <FilterPill active={purchaseFilter === 'all'} onClick={() => setPurchaseFilter('all')}>All</FilterPill>
                  <FilterPill active={purchaseFilter === 'paid'} onClick={() => setPurchaseFilter('paid')}>Paid</FilterPill>
                  <FilterPill active={purchaseFilter === 'pending'} onClick={() => setPurchaseFilter('pending')}>Pending</FilterPill>
                  <div className="relative">
                    <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-neutral-500" />
                    <Input
                      value={purchaseQuery}
                      onChange={(e) => setPurchaseQuery(e.target.value)}
                      placeholder="Search email / plan / order id"
                      className="h-9 w-56 pl-8 text-xs"
                      data-testid="activity-purchases-search"
                    />
                  </div>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full min-w-[720px] text-sm">
                  <thead className="text-left text-[11px] uppercase tracking-wider text-neutral-500">
                    <tr>
                      <th className="pb-2 pr-3">Buyer</th>
                      <th className="pb-2 pr-3">Plan</th>
                      <th className="pb-2 pr-3">Amount</th>
                      <th className="pb-2 pr-3">Status</th>
                      <th className="pb-2 pr-3">Gateway</th>
                      <th className="pb-2 pr-3">Created</th>
                      <th className="pb-2 pr-3">Verified</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-neutral-800">
                    {filteredPurchases.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="py-10 text-center text-neutral-500">
                          No orders match this filter.
                        </td>
                      </tr>
                    ) : (
                      filteredPurchases.map((o) => (
                        <tr key={o.id} className="align-top text-neutral-200" data-testid={`activity-purchase-row-${o.id}`}>
                          <td className="py-3 pr-3">
                            <div className="font-medium">{o.buyer_name || '—'}</div>
                            <div className="flex items-center gap-1 text-[11px] text-neutral-500">
                              <Mail className="h-3 w-3" />
                              {o.buyer_email || '—'}
                            </div>
                          </td>
                          <td className="py-3 pr-3">
                            <Badge variant="outline" className="border-red-500/40 text-red-300">
                              {o.plan_id}
                            </Badge>
                            <div className="mt-1 text-[11px] text-neutral-500">{o.plan_name}</div>
                          </td>
                          <td className="py-3 pr-3 font-semibold">{RUPEE(o.amount)}</td>
                          <td className="py-3 pr-3">
                            <StatusPill status={o.status} />
                          </td>
                          <td className="py-3 pr-3 text-xs text-neutral-400">
                            {o.payment_gateway || '—'}
                          </td>
                          <td className="py-3 pr-3 text-xs text-neutral-400">{DATE(o.created_at)}</td>
                          <td className="py-3 pr-3 text-xs text-neutral-400">{DATE(o.verified_at)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="users" className="space-y-3">
          <Card className="border-neutral-800 bg-neutral-950/70">
            <CardContent className="space-y-4 p-5 sm:p-6">
              <div className="flex flex-wrap items-center gap-3">
                <div className="font-display text-base font-semibold">Platform activity</div>
                <Badge variant="secondary" data-testid="activity-users-count">
                  {filteredUsers.length} / {users.length}
                </Badge>
                <div className="relative ml-auto">
                  <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-neutral-500" />
                  <Input
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    placeholder="Search email / name"
                    className="h-9 w-56 pl-8 text-xs"
                    data-testid="activity-users-search"
                  />
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full min-w-[820px] text-sm">
                  <thead className="text-left text-[11px] uppercase tracking-wider text-neutral-500">
                    <tr>
                      <th className="pb-2 pr-3">User</th>
                      <th className="pb-2 pr-3">Signup</th>
                      <th className="pb-2 pr-3">Last login</th>
                      <th className="pb-2 pr-3">Tests</th>
                      <th className="pb-2 pr-3">Spend</th>
                      <th className="pb-2 pr-3">Active plans</th>
                      <th className="pb-2 pr-3">Login via</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-neutral-800">
                    {filteredUsers.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="py-10 text-center text-neutral-500">
                          No users match this search.
                        </td>
                      </tr>
                    ) : (
                      filteredUsers.map((u) => (
                        <tr key={u.id} className="align-top text-neutral-200" data-testid={`activity-user-row-${u.id}`}>
                          <td className="py-3 pr-3">
                            <div className="font-medium">{u.name || '—'}</div>
                            <div className="flex items-center gap-1 text-[11px] text-neutral-500">
                              <Mail className="h-3 w-3" />
                              {u.email}
                            </div>
                          </td>
                          <td className="py-3 pr-3 text-xs text-neutral-400">
                            <div className="flex items-center gap-1">
                              <CalendarClock className="h-3 w-3" />
                              {SHORT_DATE(u.created_at)}
                            </div>
                          </td>
                          <td className="py-3 pr-3 text-xs text-neutral-400">{DATE(u.last_login_at)}</td>
                          <td className="py-3 pr-3">
                            <div className="font-semibold">{u.tests_count}</div>
                            <div className="text-[11px] text-neutral-500">last {SHORT_DATE(u.last_test_at)}</div>
                          </td>
                          <td className="py-3 pr-3">
                            <div className="font-semibold">{RUPEE(u.spend_total)}</div>
                            <div className="text-[11px] text-neutral-500">{u.paid_orders} orders</div>
                          </td>
                          <td className="py-3 pr-3">
                            {u.active_plans?.length ? (
                              <div className="flex flex-wrap gap-1">
                                {u.active_plans.map((p) => (
                                  <Badge
                                    key={p}
                                    className="bg-emerald-600/80 text-[10px] font-medium text-white hover:bg-emerald-600"
                                  >
                                    {p}
                                  </Badge>
                                ))}
                              </div>
                            ) : (
                              <span className="text-xs text-neutral-500">—</span>
                            )}
                          </td>
                          <td className="py-3 pr-3 text-xs">
                            {u.google_linked ? (
                              <Badge variant="outline" className="border-sky-500/40 text-sky-300">Google</Badge>
                            ) : u.last_login_method === 'password' ? (
                              <Badge variant="outline" className="border-neutral-700 text-neutral-300">Email</Badge>
                            ) : (
                              <span className="text-neutral-500">—</span>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function SummaryCard({ icon, label, value, sub, accent, testId }) {
  return (
    <Card
      className="relative overflow-hidden border-neutral-800 bg-neutral-950/70"
      data-testid={testId}
    >
      <div
        aria-hidden
        className={`pointer-events-none absolute inset-0 opacity-20 bg-gradient-to-br ${accent}`}
      />
      <CardContent className="relative space-y-1 p-5">
        <div className="flex items-center justify-between">
          <div className="text-xs font-medium uppercase tracking-wider text-neutral-400">
            {label}
          </div>
          <div className="grid h-8 w-8 place-items-center rounded-lg bg-white/10 text-white">
            {icon}
          </div>
        </div>
        <div className="font-display text-3xl font-bold tracking-tight text-white drop-shadow-sm">{value}</div>
        <div className="flex items-center gap-1 text-[11px] text-neutral-500">
          <TrendingUp className="h-3 w-3" />
          {sub}
        </div>
      </CardContent>
    </Card>
  );
}

function FilterPill({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`h-8 rounded-full border px-3 text-xs transition ${
        active
          ? 'border-red-500/60 bg-red-500/15 text-red-300'
          : 'border-neutral-800 bg-neutral-900/40 text-neutral-400 hover:text-white'
      }`}
    >
      {children}
    </button>
  );
}

function StatusPill({ status }) {
  const s = (status || '').toLowerCase();
  const map = {
    paid: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300',
    pending: 'border-amber-500/40 bg-amber-500/10 text-amber-300',
    failed: 'border-red-500/40 bg-red-500/10 text-red-300',
  };
  const cls = map[s] || 'border-neutral-700 bg-neutral-800 text-neutral-300';
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium ${cls}`}>
      {status || 'unknown'}
    </span>
  );
}
